// Material UI Components
import { Box, Grid, IconButton, LinearProgress, Stack } from "@mui/material";
import React from "react";

// Icons
import RedBackButton from "./icons/RedBackButton";
import { TransactionDisclosureModal } from "src/components/disclosures/TransactionDisclosureModal";
import useTransactionDisclosure, {
  type DisclosureMetadata,
} from "src/utils/hooks/useTransactionDisclosure";

// Redux
import api from "src/store/features/api";
import { useAppDispatch } from "src/store/store";
import { DcaStrategySummaryRes } from "src/store/features/api/generated";
import {
  setErrorMessage,
  setModalState,
  setSpendLimitError,
  useDcaSlice,
} from "src/store/features/dca.slice";

// Components
import StyledButton from "../dashboard/StyledButton";
import { StyledDivider } from "./styled-components/TableStyledComponents";
import BalanceTooltip from "../wallets/header/BalanceTooltip";
import { Info } from "@mui/icons-material";
import { StyledBoldTitle, StyledDisclaimerText, SummaryLabel } from "./styled-components/DcaFields";

// Utils
import { formatCurrency } from "src/utils/helpers/PriceAndUnitsHelpers";

// Hooks
import useSummaryConfig from "./hooks/useSummaryConfig";
import { calculateSecondOrderDate } from "./utils/tools";
import { trackEvent } from "src/Amplitude";

export const brokerFeeHelperText =
  "Your trade has one all-inclusive broker fee covering costs associated with slippage, foreign exchange, and labour involved. A further breakdown will be provided in your invoice.";

export default function OrderSummary() {
  const { summary, endDate, spendLimit } = useDcaSlice();
  const dispatch = useAppDispatch();
  const [postStrategy, { isLoading }] = api.useCreateDcaStrategyMutation();

  const { startDate, summaryDetails, nextOrderDifference, totalValue, sellToken, frequency } =
    useSummaryConfig();

  // ================= Transaction-specific disclosure =================
  const [showDisclosureModal, setShowDisclosureModal] = React.useState(false);
  const [pendingSubmit, setPendingSubmit] = React.useState(false);

  // Don't auto-open disclosure - we'll control it manually
  const {
    transactionDisclosure,
    requiresDisclosure,
    isLoading: isDisclosureLoading,
    disclosurePayload,
    modalProps,
    closeModal: handleDisclosureClose,
  } = useTransactionDisclosure((disclosureId: string, metadata?: DisclosureMetadata) => {
    if (pendingSubmit) {
      processCreateStrategy(metadata);
    }
    setPendingSubmit(false);
    setShowDisclosureModal(false);
  }, "dca_strategy");

  // Override the modal open state to control it manually
  const disclosureModalProps = {
    ...modalProps,
    open: showDisclosureModal && !!transactionDisclosure,
    onClose: () => {
      // If disclosure is closed without accepting, just close the disclosure modal
      setShowDisclosureModal(false);
      setPendingSubmit(false);
      handleDisclosureClose();
    },
  };

  // Process strategy creation (after disclosure is accepted or when no disclosure required)
  const processCreateStrategy = async (disclosureMetadata?: DisclosureMetadata) => {
    const timezone =
      typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : "UTC";
    const disclosureFields = (disclosurePayload || disclosureMetadata) && {
      disclosureRequestDateTime:
        disclosurePayload?.disclosureRequestDateTime ||
        disclosureMetadata?.disclosureRequestDateTime,
      disclosureAcceptanceDateTime:
        disclosurePayload?.disclosureAcceptanceDateTime ||
        disclosureMetadata?.disclosureAcceptanceDateTime,
      timezone: timezone,
    };

    const dcaStrategySummaryRes = {
      ...(summary as DcaStrategySummaryRes),
      ...(disclosureFields ?? {}),
    };
    await postStrategy({ dcaStrategySummaryRes })
      .unwrap()
      .then(() => {
        trackEvent("User Placed order", { ...dcaStrategySummaryRes }, "DCA");
        dispatch(setModalState("success"));
      })
      .catch(() => {
        window.alert("Error creating strategy");
      });
  };

  const createStrategy = async () => {
    // Don't proceed while the disclosure is still resolving — a null transactionDisclosure
    // (and thus requiresDisclosure === false) means "unknown", not "not required".
    if (isDisclosureLoading) {
      return;
    }

    // Check if disclosure is required (MN/CT and not already accepted today)
    if (requiresDisclosure && !showDisclosureModal) {
      // Store the submission intent and show disclosure modal
      setPendingSubmit(true);
      setShowDisclosureModal(true);
      return;
    }

    // If no disclosure required, proceed directly (no metadata)
    await processCreateStrategy(undefined);
  };

  const subsequentOrderDisclaimerText = `You are currently short of ${formatCurrency(
    nextOrderDifference ?? 0,
    sellToken,
  )} for your subsequent order. Please make sure you deposit this amount
          before ${calculateSecondOrderDate(
            startDate,
            frequency,
          )} for your plan to execute successfully.`;

  const noLimitDisclaimerText =
    "Please note that you currently have no end date or spend limit. You may amend this in the previous step. By confirming, you acknowledge that your scheduled orders will repeat until your balance is depleted or you cancel your plan.";

  const DisclaimerBox = ({ text }: { text: string }) => {
    return (
      <Box sx={{ borderRadius: "10px", background: "#F8F9F9", padding: "8px", mt: 2 }}>
        <StyledDisclaimerText>{text}</StyledDisclaimerText>
      </Box>
    );
  };

  return (
    <>
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
        mb={2}
        py={1.5}
        sx={{ borderBottom: "1px solid #E5E7E8" }}>
        <IconButton
          aria-label="back"
          onClick={() => {
            dispatch(setModalState("create plan"));
            dispatch(setErrorMessage(""));
            dispatch(setSpendLimitError(""));
          }}>
          <RedBackButton />
        </IconButton>
        <StyledBoldTitle>{"Your Plan Details"}</StyledBoldTitle>
        <div></div>
      </Stack>
      <Grid container mb={2} pb={2} spacing={2}>
        {summaryDetails.map(({ label, content }: { label: string; content: string }) => (
          <>
            {content && (
              <>
                <Grid item xs={4}>
                  <SummaryLabel sx={{ fontWeight: 600 }}>
                    {label === "Broker Fee" ? (
                      <div style={{ display: "flex", gap: 4 }}>
                        <SummaryLabel sx={{ minWidth: "fit-content" }}>Broker Fee</SummaryLabel>
                        <BalanceTooltip message={brokerFeeHelperText} contrast>
                          <Info style={{ fontSize: "17px" }} />
                        </BalanceTooltip>
                      </div>
                    ) : (
                      label
                    )}
                  </SummaryLabel>
                </Grid>
                <Grid item xs={6}>
                  <SummaryLabel>{content}</SummaryLabel>
                </Grid>
              </>
            )}
          </>
        ))}
      </Grid>
      <StyledDivider />
      {totalValue && (
        <Stack direction="row" justifyContent="space-between">
          <StyledBoldTitle>Total</StyledBoldTitle>
          <StyledBoldTitle>{formatCurrency(totalValue ?? 0, sellToken)}</StyledBoldTitle>
        </Stack>
      )}
      {/* No subsequent funds warning */}
      {nextOrderDifference && <DisclaimerBox text={subsequentOrderDisclaimerText} />}

      {/* No limits warning */}
      {!spendLimit && !endDate && <DisclaimerBox text={noLimitDisclaimerText} />}

      <StyledButton onClick={createStrategy} disabled={isLoading} sx={{ mt: 2 }}>
        Create Plan
      </StyledButton>
      {isLoading && (
        <LinearProgress
          sx={{
            marginTop: "14px",
            width: "95%",
            marginLeft: "8px",
          }}
        />
      )}

      {/* TRANSACTION-SPECIFIC DISCLOSURE MODAL */}
      {transactionDisclosure && <TransactionDisclosureModal {...disclosureModalProps} />}
    </>
  );
}
